import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

// One continuous editorial field: archival paper base, feathered document
// regions and two portrait slots that slowly crossfade between sitters.
interface Slot { id: string; style: CSSProperties; tone: string; drift: string; mask: string; images: string[] }

const SLOTS: Slot[] = [
  {
    id: 'portrait-ul',
    style: { left: '-4%', top: '-6%', width: '44%', height: '78%' },
    tone: 'opacity-[0.68]',
    drift: 'field-drift-a',
    mask: 'field-mask-ul-soft',
    images: [
      'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-portrait-ul.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-portrait-ul-alt.webp',
    ],
  },
  {
    id: 'portrait-r',
    style: { left: '71%', top: '22%', width: '25%', height: '42%' },
    tone: 'opacity-[0.5]',
    drift: 'field-drift-c',
    mask: 'field-mask-portrait-soft',
    images: [
      'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-portrait-r.webp',
      'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-portrait-r-alt.webp',
    ],
  },
];

const DOCS = [
  {
    id: 'doc-ll',
    src: 'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-doc-ll.webp',
    style: { left: '-4%', top: '58%', width: '42%', height: '48%' } as CSSProperties,
    tone: 'opacity-[0.56] field-doc',
    drift: 'field-drift-d',
  },
  {
    id: 'doc-lr',
    src: 'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-doc-lr.webp',
    style: { left: '64%', top: '64%', width: '38%', height: '40%' } as CSSProperties,
    tone: 'opacity-[0.46] field-doc',
    drift: 'field-drift-b',
  },
];

const BASE = 'https://public.readdy.ai/ai/img_res/sandbox/9ad2232c3293228d5474f3b734806e42/4de54800-128f-4934-bf37-00c62bf50712_avenor-hero-base-field.webp';

const HOLD_MS = 9000;

export default function HeroField() {
  // index of the visible sitter per slot; only one slot changes per tick
  const [active, setActive] = useState<number[]>([0, 0]);
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const loadedRef = useRef<Record<string, boolean>>({});

  const markLoaded = (src: string) => {
    if (loadedRef.current[src]) return;
    loadedRef.current[src] = true;
    setLoaded((prev) => ({ ...prev, [src]: true }));
  };

  // preload every sitter up front so a crossfade never reveals a half-loaded image
  useEffect(() => {
    SLOTS.forEach((s) => s.images.forEach((src) => {
      const img = new Image();
      img.onload = () => markLoaded(src);
      img.src = src;
    }));
  }, []);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let turn = 0;
    const id = window.setInterval(() => {
      const slot = turn % SLOTS.length;
      turn += 1;
      setActive((prev) => {
        const next = (prev[slot] + 1) % SLOTS[slot].images.length;
        // skip this turn if the next sitter is not ready yet
        if (!loadedRef.current[SLOTS[slot].images[next]]) return prev;
        return prev.map((v, i) => (i === slot ? next : v));
      });
    }, HOLD_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <>
      <img src={BASE} alt="" className="archival-img field-doc absolute inset-0 h-full w-full object-cover object-top opacity-[0.32] mix-blend-multiply" />
      {DOCS.map((d) => (
        <div key={d.id} className={`field-region halftone field-mask-soft field-desktop-only ${d.tone}`} style={{ position: 'absolute', ...d.style }}>
          <img src={d.src} alt="" className={`archival-img h-[112%] w-full object-cover object-top mix-blend-multiply ${d.drift}`} />
        </div>
      ))}
      {SLOTS.map((s, si) => (
        <div key={s.id} className={`field-region halftone field-portrait ${si === 0 ? 'field-slot-main' : 'field-desktop-only'} ${s.mask} ${s.tone}`} style={{ position: 'absolute', ...s.style }}>
          {s.images.map((src, ii) => (
            <img
              key={src}
              src={src}
              alt=""
              onLoad={() => markLoaded(src)}
              className={`archival-img absolute inset-0 h-[112%] w-full object-cover object-top mix-blend-multiply transition-opacity duration-[4000ms] ease-in-out ${s.drift} ${active[si] === ii && loaded[src] ? 'opacity-100' : 'opacity-0'}`}
            />
          ))}
        </div>
      ))}
      <div className="field-veil absolute inset-0" />
    </>
  );
}